import { Component, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Auth, AuthCredential, GoogleAuthProvider, linkWithCredential, signInWithEmailAndPassword, signOut, UserCredential } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { AppConfig } from '../../../../config/appConfig';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/user.model';
import { FirestoreService } from '../../../services/firestore.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { PublicBrandingService } from '../../../services/public-branding.service';
import { AuthOnboardingService } from '../../../services/auth-onboarding.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  title = AppConfig.title;
  loginForm: FormGroup;
  user$: Observable<User | null>;
  redirectTo: string | null = null;
  accountAlias: string | null = null;
  readonly isSubmitting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  private pendingGoogleCredential: AuthCredential | null = null;
  readonly brandingProfile = toSignal(this.publicBrandingService.brandingProfile$, { initialValue: null });

  constructor(
    private readonly fb: FormBuilder,
    private readonly auth: Auth,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly firestoreService: FirestoreService,
    private readonly authService: AuthService,
    private readonly subscriptionService: SubscriptionService,
    private readonly publicBrandingService: PublicBrandingService,
    private readonly authOnboardingService: AuthOnboardingService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    this.user$ = this.authService.user$;
  }

  ngOnInit(): void {
    this.redirectTo = this.route.snapshot.queryParamMap.get('redirectTo');
    this.accountAlias = this.route.snapshot.queryParamMap.get('ref');
  }

  onLogin() {
    if (this.loginForm.invalid) return;

    const { email, password } = this.loginForm.value;
    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    from(signInWithEmailAndPassword(this.auth, email.trim(), password)).pipe(
      switchMap(cred => this.continuePasswordLogin(cred, email)),
      tap(() => this.navigateAfterLogin()),
      catchError(err => {
        console.error('Login error:', err);
        this.errorMessage.set(this.friendlyAuthError(err));
        return EMPTY;
      }),
      finalize(() => this.isSubmitting.set(false))
    ).subscribe();
  }

  private continuePasswordLogin(cred: UserCredential, email: string): Observable<UserCredential> {
    return this.ensureUserAccess(cred.user.uid).pipe(
      switchMap(canAccess => canAccess ? of(cred) : EMPTY),
      switchMap(accessibleCredential => this.linkPendingGoogleCredential(accessibleCredential)),
      switchMap(accessibleCredential => {
        if (!accessibleCredential.user.emailVerified) {
          return this.firestoreService.getUserProfile(accessibleCredential.user.uid).pipe(
            take(1),
            tap(profile => {
              if (!profile) void this.router.navigate(['/verify-email'], { queryParams: this.loginLinkQueryParams });
            }),
            switchMap(profile => profile ? of(accessibleCredential) : EMPTY)
          );
        }
        return from(this.firestoreService.updateLastLogin(accessibleCredential.user.uid)).pipe(
          switchMap(() => this.logLoginActivity(accessibleCredential.user.uid, email)),
          map(() => accessibleCredential)
        );
      })
    );
  }

  onGoogleSignIn(): void {
    if (this.isSubmitting()) return;
    this.isSubmitting.set(true);
    this.errorMessage.set(null);
    from(this.authOnboardingService.signInWithGoogle()).pipe(
      switchMap(user => from(this.authOnboardingService.ensureUserProvisioned(user))),
      tap(() => this.navigateAfterLogin()),
      catchError(err => {
        if (err?.code === 'auth/account-exists-with-different-credential') {
          this.pendingGoogleCredential = GoogleAuthProvider.credentialFromError(err);
          const googleEmail = typeof err?.customData?.email === 'string' ? err.customData.email : '';
          if (googleEmail) this.loginForm.patchValue({ email: googleEmail });
          this.errorMessage.set(
            'This email already has a password login. Enter that password and select Log In once to securely connect Google. Both login methods will remain available.'
          );
        } else {
          this.errorMessage.set(this.friendlyAuthError(err));
        }
        return EMPTY;
      }),
      finalize(() => this.isSubmitting.set(false))
    ).subscribe();
  }

  private logLoginActivity(userId: string, email: string): Observable<unknown> {
    return this.firestoreService.logUserActivity(userId, 'login', {
      email,
      redirectTo: this.redirectTo || null
    }).pipe(
      catchError(err => {
        console.error('Failed to log activity:', err);
        return of(null);
      })
    );
  }

  private linkPendingGoogleCredential(cred: UserCredential): Observable<UserCredential> {
    if (!this.pendingGoogleCredential) return of(cred);

    return from(linkWithCredential(cred.user, this.pendingGoogleCredential)).pipe(
      tap(() => this.pendingGoogleCredential = null),
      map(() => cred)
    );
  }

  private navigateAfterLogin(): void {
    if (this.redirectTo) {
      this.router.navigateByUrl(this.redirectTo);
      return;
    }

    const selectedPlan = this.subscriptionService.getSelectedPlan();
    if (selectedPlan) {
      this.router.navigate(['/checkout']);
      return;
    }

    this.router.navigate(['/dashboard']);
  }

  goToDashboard() {
    if (this.redirectTo) {
      this.router.navigateByUrl(this.redirectTo);
      return;
    }
    this.router.navigate(['/dashboard']);
  }

  onLogout() {
    const uid = this.auth.currentUser?.uid;
    const email = this.auth.currentUser?.email ?? null;

    const logout$ = uid
      ? this.firestoreService.logUserActivity(uid, 'logout', { email }).pipe(
          catchError(err => {
            console.error('Failed to log activity:', err);
            return of(null);
          }),
          switchMap(() => from(this.authService.logout()))
        )
      : from(this.authService.logout());

    logout$.pipe(
      catchError(err => {
        console.error('Logout error:', err);
        alert('Logout failed: ' + err.message);
        return EMPTY;
      })
    ).subscribe();
  }

  getDisplayName(user: User): string {
    return (
      user.displayName ||
      user.fullName ||
      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
      user.email
    );
  }

  getInitials(user: User): string {
    const displayName = this.getDisplayName(user);
    const parts = displayName.split(' ').filter(Boolean);
    const initials = parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('');
    return initials || (user.email ? user.email.charAt(0).toUpperCase() : '?');
  }

  private ensureUserAccess(userId: string): Observable<boolean> {
    return this.firestoreService.getUserProfile(userId).pipe(
      take(1),
      switchMap(userData => {
        const isBlocked = !!userData?.['isBlocked'];
        const isBanned = !!userData?.['isBanned'];
        if (!isBlocked && !isBanned) {
          return of(true);
        }

        const messageOverride = typeof userData?.['accessStatusMessage'] === 'string'
          ? userData['accessStatusMessage'].trim()
          : '';
        const defaultMessage = isBanned
          ? 'Your account has been banned. Please contact support.'
          : 'Your account has been blocked. Please contact support.';
        const message = messageOverride || defaultMessage;

        return from(signOut(this.auth)).pipe(
          tap(() => alert(message)),
          switchMap(() => of(false))
        );
      })
    );
  }

  private friendlyAuthError(error: { code?: string; message?: string }): string {
    if (error.code === 'auth/popup-closed-by-user') return 'Google sign-in was cancelled.';
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      return 'The email address or password is incorrect.';
    }
    if (error.code === 'auth/too-many-requests') return 'Too many attempts. Please wait a moment and try again.';
    return error.message ?? 'Sign-in failed. Please try again.';
  }

  get logoUrl(): string {
    const logoUrl = this.brandingProfile()?.companyInformation?.logoUrl?.trim() ?? '';
    return logoUrl || 'assets/yachtapp-logo-dark.png';
  }

  get logoAlt(): string {
    const companyName = this.brandingProfile()?.companyInformation?.name?.trim() ?? '';
    return companyName ? `${companyName} Logo` : `${this.title} Logo`;
  }

  get loginLinkQueryParams(): Record<string, string> | null {
    const params: Record<string, string> = {};
    if (this.redirectTo) {
      params['redirectTo'] = this.redirectTo;
    }
    if (this.accountAlias) {
      params['ref'] = this.accountAlias;
    }
    return Object.keys(params).length ? params : null;
  }
}
